import React from 'react';
import useGameStore from '../engine/store';

function formatMoney(value) {
  if (Math.abs(value) >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (Math.abs(value) >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

export default function TopBar() {
  const {
    month, totalCapital, liquidity, riskScore, activeLoans,
    economyState, flashEffect,
    pendingLoans, advanceMonth, bankHealth,
  } = useGameStore();

  const allDecided = pendingLoans.every(l => l.approved !== null);
  const hasLoans = pendingLoans.length > 0;
  const decidedCount = pendingLoans.filter(l => l.approved !== null).length;

  const ecoConfig = {
    boom: { emoji: '📈', color: '#00ff88' },
    normal: { emoji: '📊', color: '#00aaff' },
    recession: { emoji: '📉', color: '#ff3366' },
  };
  const eco = ecoConfig[economyState] || ecoConfig.normal;

  const healthConfig = {
    healthy: { emoji: '💚', color: '#00ff88' },
    warning: { emoji: '⚠️', color: '#ffcc00' },
    critical: { emoji: '🚨', color: '#ff3366' },
  };
  const health = healthConfig[bankHealth] || healthConfig.healthy;

  return (
    <div className={`
      h-14 bg-dark-800/80 backdrop-blur-md border-b border-white/5 flex items-center px-4 gap-4
      transition-all duration-500
      ${flashEffect === 'green' ? 'animate-flash-green' : ''}
      ${flashEffect === 'red' ? 'animate-flash-red' : ''}
    `}>
      {/* Logo */}
      <div className="flex items-center gap-2 mr-2">
        <span className="text-xl">🏦</span>
        <span className="font-black text-transparent bg-clip-text bg-gradient-to-r from-neon-green to-neon-blue text-sm hidden md:block">
          BANK EMPIRE
        </span>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-5 flex-1 overflow-x-auto text-sm">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500">📅</span>
          <span className="font-mono text-white">Mo {month}</span>
        </div>

        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500">🏦</span>
          <span className="font-mono text-neon-blue">{formatMoney(totalCapital)}</span>
        </div>

        <div className="flex items-center gap-1.5 hidden sm:flex">
          <span className="text-xs text-gray-500">💵</span>
          <span className="font-mono text-neon-green">{formatMoney(liquidity)}</span>
        </div>

        <div className="flex items-center gap-1.5 hidden md:flex">
          <span className="text-xs text-gray-500">📋</span>
          <span className="font-mono text-white">{activeLoans.length} loans</span>
        </div>

        <div className="flex items-center gap-1.5 hidden lg:flex">
          <span className="text-xs text-gray-500">⚠️</span>
          <span className="font-mono" style={{
            color: riskScore <= 30 ? '#00ff88' : riskScore <= 60 ? '#ffcc00' : '#ff3366'
          }}>{riskScore}</span>
        </div>

        <div className="flex items-center gap-1.5 hidden lg:flex">
          <span style={{ color: eco.color }}>{eco.emoji}</span>
        </div>

        <div className="flex items-center gap-1 hidden lg:flex">
          <span>{health.emoji}</span>
        </div>
      </div>

      {/* Progress indicator */}
      {hasLoans && (
        <div className="flex items-center gap-2 mr-2">
          <span className="text-xs text-gray-500">{decidedCount}/{pendingLoans.length}</span>
          <div className="w-16 h-1.5 rounded-full bg-dark-600 overflow-hidden">
            <div
              className="h-full rounded-full bg-neon-green transition-all duration-300"
              style={{ width: `${(decidedCount / pendingLoans.length) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Advance button */}
      <button
        onClick={advanceMonth}
        disabled={!allDecided || !hasLoans}
        className={`py-2 px-5 rounded-lg text-sm font-bold transition-all active:scale-95 flex items-center gap-2 ${
          allDecided && hasLoans
            ? 'bg-gradient-to-r from-neon-green/20 to-neon-blue/20 border border-neon-green/40 text-neon-green hover:from-neon-green/30 hover:to-neon-blue/30 shadow-lg shadow-neon-green/10'
            : 'bg-dark-600 border border-white/5 text-gray-600 cursor-not-allowed'
        }`}
      >
        <span>⏩</span>
        <span className="hidden sm:inline">End Month</span>
      </button>
    </div>
  );
}
